import type { InputHTMLAttributes, ReactNode, Ref } from 'react';
import styles from './Input.module.css';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size?: 'sm' | 'md' | 'lg';
  /** Marks the value as invalid (sets `aria-invalid`). */
  invalid?: boolean;
  /** Decoration or control inside the field before the text, e.g. an icon. */
  startAdornment?: ReactNode;
  /** Decoration or control inside the field after the text, e.g. a reveal button. */
  endAdornment?: ReactNode;
  ref?: Ref<HTMLInputElement>;
}

export function Input({
  size = 'md',
  invalid = false,
  startAdornment,
  endAdornment,
  className,
  type = 'text',
  ...rest
}: InputProps) {
  const classes = [
    styles.input,
    styles[size],
    invalid && styles.invalid,
    startAdornment !== undefined && styles.hasStart,
    endAdornment !== undefined && styles.hasEnd,
    className,
  ]
    .filter(Boolean)
    .join(' ');
  const input = (
    <input type={type} className={classes} aria-invalid={invalid || undefined} {...rest} />
  );
  if (startAdornment === undefined && endAdornment === undefined) return input;
  return (
    <span className={styles.control}>
      {startAdornment !== undefined && <span className={styles.start}>{startAdornment}</span>}
      {input}
      {endAdornment !== undefined && <span className={styles.end}>{endAdornment}</span>}
    </span>
  );
}
